import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SelectField from 'components/forms/selectField/SelectField';
import { AirbnbGuestRequirements } from 'models/AirbnbGuestRequirements';
import { BOOKING_TYPES } from 'models/BookingType';
import {
  PropertySettingsMainTabFormValues,
  airbnbSettingsGuestRequirementsDefaultValues,
} from '../../PropertySettingsMainTab.schema';

const optionsByBookingType: Record<string, AirbnbGuestRequirements[]> = {
  [BOOKING_TYPES.BOOKING_REQUEST]: [AirbnbGuestRequirements.OFF],
  [BOOKING_TYPES.INSTANT_BOOKING]: [
    AirbnbGuestRequirements.EVERYONE,
    AirbnbGuestRequirements.WELL_REVIEWED_GUESTS,
  ],
};

const GuestRequirementsField = () => {
  const { t } = useTranslation();
  const { control, setValue } =
    useFormContext<PropertySettingsMainTabFormValues>();
  const bookingType = useWatch({
    control,
    name: 'airbnbSettings.bookingType',
  });
  const guestRequirement = useWatch({
    control,
    name: 'airbnbSettings.guestRequirements',
  });

  const availableValues = useMemo(
    () => (bookingType ? optionsByBookingType[bookingType] : []),
    [bookingType],
  );

  const options = availableValues.map((value) => ({
    label: t(
      `pageProperty.mainSettings.airbnbSettings.guestRequirementsOptions.${value}`,
    ),
    value,
  }));

  /**
   * If the selected guest requirement is not part of the available options, we then change its value to be the default
   * guest requirement for the given booking type.
   */
  useEffect(() => {
    if (
      bookingType &&
      guestRequirement &&
      availableValues.length &&
      !availableValues.includes(guestRequirement as AirbnbGuestRequirements)
    ) {
      const guestRequirementsDefaultValue =
        airbnbSettingsGuestRequirementsDefaultValues[bookingType];

      setValue(
        'airbnbSettings.guestRequirements',
        guestRequirementsDefaultValue,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }
  }, [availableValues, bookingType, guestRequirement]);

  return (
    <SelectField
      label={t('pageProperty.mainSettings.airbnbSettings.guestRequirements')}
      name="airbnbSettings.guestRequirements"
      options={options}
      colSmInput={7}
      colSmLabel={5}
    />
  );
};

export default GuestRequirementsField;
